import { getAccessRequestCapability } from '@atlassian/confluence-cross-join-third-party-nudges/services/request-capabilities';
import { getUsersFromCollaborationGraph } from '@atlassian/confluence-cross-join-third-party-nudges/services/collaborators';
import { getCollaboratorsWithJiraAccess } from '@atlassian/confluence-cross-join-third-party-nudges/services/collaborators-perm-check';
import { isSite7DaysOld } from '@atlassian/confluence-cross-join-third-party-nudges/services/site-eligibility';
import { hasAccessToProduct } from '@atlassian/confluence-cross-join-third-party-nudges/services/user-perm-check';
import type { Collaborators } from '@atlassian/confluence-cross-join-third-party-nudges/types';

import { increaseFeatureVisitedCount } from './feature-visited.ts';

export type IneligibilityReason =
	| 'NEW_SITE_LESS_THAN_7_DAYS_OLD'
	| 'ALREADY_HAVE_ACCESS'
	| 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS'
	| 'NO_COLLABORATORS_FOUND'
	| 'COLLABORATORS_HAVE_NO_ACCESS';

export type ResolverInput = {
	userId: string;
	cloudId: string;
};

export type ResolverResult =
	| {
			ineligibilityReason: IneligibilityReason;
	  }
	| {
			ineligibilityReason: null;
			collaborators: Collaborators;
			accessCapability: 'DIRECT_ACCESS' | 'REQUEST_ACCESS';
	  };

export const resolveCrossJoinNudges = async ({
	userId,
	cloudId,
}: ResolverInput): Promise<ResolverResult> => {
	if (await hasAccessToProduct('jira-software', cloudId)) {
		return {
			ineligibilityReason: 'ALREADY_HAVE_ACCESS',
		};
	}

	const collaborators = await getUsersFromCollaborationGraph(
		'confluenceCrossJoin3PNudges',
		cloudId,
		userId,
	);
	if (collaborators === undefined || collaborators.length === 0) {
		return {
			ineligibilityReason: 'NO_COLLABORATORS_FOUND',
		};
	}

	const collaboratorsWithJiraAccess = await getCollaboratorsWithJiraAccess(cloudId, collaborators);
	if (collaboratorsWithJiraAccess.length === 0) {
		return {
			ineligibilityReason: 'COLLABORATORS_HAVE_NO_ACCESS',
		};
	}

	const accessCapability = await getAccessRequestCapability('jira-software', cloudId);
	if (accessCapability !== 'DIRECT_ACCESS' && accessCapability !== 'REQUEST_ACCESS') {
		return {
			ineligibilityReason: 'NOT_ELIGIBLE_FOR_REQUEST_ACCESS',
		};
	}

	if (!(await isSite7DaysOld('jira-software', cloudId))) {
		return {
			ineligibilityReason: 'NEW_SITE_LESS_THAN_7_DAYS_OLD',
		};
	}

	increaseFeatureVisitedCount();

	return {
		ineligibilityReason: null,
		collaborators: collaboratorsWithJiraAccess,
		accessCapability,
	};
};
